import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
  customerCreateSchema,
  customerUpdateSchema,
  type CustomerCreateInput,
  type CustomerRow,
  type CustomerUpdateInput,
} from '@runik/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { toast } from '../ui/sonner';
import { ApiError } from '../../lib/auth/client';
import { createCustomer, updateCustomer } from '../../lib/queries/customers';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: CustomerRow;
  onCreated?: (resp: { activationUrl: string; expiresAt: string }) => void;
}

export function CustomerFormDialog({ open, onOpenChange, customer, onCreated }: Props) {
  const editing = !!customer;
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerCreateInput>({
    resolver: zodResolver(editing ? customerUpdateSchema : customerCreateSchema),
    defaultValues: customer
      ? {
          companyName: customer.companyName,
          billingEmail: customer.billingEmail ?? undefined,
          contactName: customer.contactName ?? undefined,
          taxId: customer.taxId ?? undefined,
        }
      : {},
  });

  useEffect(() => {
    if (open) {
      setServerError(null);
      reset(
        customer
          ? {
              companyName: customer.companyName,
              billingEmail: customer.billingEmail ?? undefined,
              contactName: customer.contactName ?? undefined,
              taxId: customer.taxId ?? undefined,
            }
          : {},
      );
    }
  }, [open, customer, reset]);

  const create = useMutation({
    mutationFn: (data: CustomerCreateInput) => createCustomer(data),
    onSuccess: (resp) => {
      toast.success('Müşteri oluşturuldu');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onCreated?.({ activationUrl: resp.invitation.activationUrl, expiresAt: resp.invitation.expiresAt });
      onOpenChange(false);
    },
  });

  const update = useMutation({
    mutationFn: (data: CustomerUpdateInput) => updateCustomer(customer!.id, data),
    onSuccess: () => {
      toast.success('Müşteri güncellendi');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
  });

  const submitting = create.isPending || update.isPending;

  const onSubmit = async (data: CustomerCreateInput) => {
    setServerError(null);
    try {
      if (editing) await update.mutateAsync(data);
      else await create.mutateAsync(data);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Bir hata oluştu');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Müşteriyi Düzenle' : 'Yeni Müşteri'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Müşteri bilgilerini güncelleyin.'
              : 'Müşteri kaydı oluşturulur ve aktivasyon linki üretilir.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="companyName">Şirket Adı *</Label>
            <Input id="companyName" autoFocus {...register('companyName')} />
            {errors.companyName && (
              <p className="text-sm text-rose-600">{errors.companyName.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="billingEmail">Fatura E-postası</Label>
            <Input id="billingEmail" type="email" {...register('billingEmail')} />
            {errors.billingEmail && (
              <p className="text-sm text-rose-600">{errors.billingEmail.message}</p>
            )}
            {!editing && (
              <p className="text-xs text-slate-500">
                Aktivasyon için kullanılır; aktivasyondan sonra giriş e-postası olur.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">İletişim Kişisi</Label>
              <Input id="contactName" {...register('contactName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxId">Vergi No</Label>
              <Input id="taxId" {...register('taxId')} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <><Spinner /> Kaydediliyor…</> : editing ? 'Kaydet' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

